import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSelector from '@/components/LanguageSelector';
import LegalModal from '@/components/LegalModal';
import bgCouple from '@/res/bg-couple.jpg';
import logoImg from '@/res/betterhusband_black.png';


const GetStarted = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [, forceUpdate] = useState(0);
  const isIt = i18n.language?.startsWith('it');
  const isEs = i18n.language?.startsWith('es');
  const lang = isIt ? 'it' : isEs ? 'es' : 'en';
  const [legalModal, setLegalModal] = useState(null);

  useEffect(() => {
    const handleLangChange = () => forceUpdate(n => n + 1);
    i18n.on('languageChanged', handleLangChange);
    return () => i18n.off('languageChanged', handleLangChange);
  }, [i18n]);
  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      backgroundImage: `url(${bgCouple})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundColor: '#0f1113',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* top bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{
          position: 'relative', zIndex: 50,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: 'max(calc(env(safe-area-inset-top, 16px) + 12px), 28px) 24px 0',
        }}
      >
        <LanguageSelector inline />
      </motion.div>

      {/* Logo + App Name */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25 }}
        style={{
          position: 'relative', zIndex: 2,
          display: 'flex', flexDirection: 'row',
          alignItems: 'center', justifyContent: 'center',
          paddingTop: 20, paddingBottom: 0,
          gap: 10,
          background: 'transparent',
          padding: '10px 20px',
          margin: '16px auto 0',
          width: 'fit-content',
        }}
      >
        <img
          src={logoImg}
          alt="BetterHusband logo"
          style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 10 }}
        />
        <span style={{
          fontFamily: 'Manrope-Variable, Manrope, sans-serif',
          fontSize: 18,
          fontWeight: 700,
          color: '#ffffff',
          letterSpacing: '0.3px',
        }}>Better Husband</span>
      </motion.div>

      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.65) 100%)',
        zIndex: 0, pointerEvents: 'none',
      }} />

      {/* Subtle warm texture glow */}
      <div style={{
        position: 'absolute', top: '20%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 380, height: 380, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(42,70,54,0.07) 0%, rgba(42,70,54,0.02) 50%, transparent 70%)',
        zIndex: 0, pointerEvents: 'none',
      }} />

      {/* ── Text Content ── */}
      <div style={{
        position: 'relative', zIndex: 2,
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '0 28px',
      }}>
        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          style={{
            fontFamily: 'Fraunces, serif',
            fontSize: 38, fontWeight: 700, lineHeight: 1.1,
            color: '#FFFFFF', margin: 0,
            textAlign: 'center', maxWidth: 320,
            letterSpacing: '-0.02em',
          }}
        >
          {isIt
            ? 'Coltiva la tua relazione'
            : isEs
              ? 'Cultiva tu relación'
              : 'Cultivate your relationship'}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 16, fontWeight: 400, lineHeight: 1.65,
            color: 'rgba(255,255,255,0.88)', textAlign: 'center',
            maxWidth: 280, marginTop: 14,
          }}
        >
          {isIt
            ? 'Piccole azioni quotidiane. Grandi risultati in coppia.'
            : isEs
              ? 'Pequeñas acciones diarias. Grandes resultados en pareja.'
              : 'Small daily actions. Great couple results.'}
        </motion.p>
      </div>

      {/* ── Bottom CTA section ── */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
        style={{
          position: 'relative', zIndex: 2,
          padding: '0 24px calc(env(safe-area-inset-bottom, 20px) + 20px)',
          maxWidth: 440, width: '100%', margin: '0 auto',
        }}
      >
        {/* Primary CTA — coral, matching landing/home */}
        <button
          onClick={() => navigate('/onboarding')}
          style={{
            width: '100%', height: 56,
            borderRadius: 5, border: 'none',
            background: '#E36A55',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600, fontSize: 13,
            color: '#fff', letterSpacing: '0.15em', textTransform: 'uppercase',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(227,106,85,0.25), 0 2px 8px rgba(0,0,0,0.06)',
            marginBottom: 12,
            transition: 'background 0.2s, transform 0.15s',
          }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; e.currentTarget.style.background = '#d45d49'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = '#E36A55'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = '#E36A55'; }}
        >
          {isIt ? 'Inizia il viaggio' : isEs ? 'Comienza el viaje' : 'Start the journey'}
        </button>

        {/* Secondary CTA — forest green outline */}
        <button
          onClick={() => navigate('/login')}
          style={{
            width: '100%', height: 50,
            borderRadius: 5,
            border: '1.5px solid rgba(255,255,255,0.45)',
            background: 'rgba(255,255,255,0.08)',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600, fontSize: 14,
            color: '#FFFFFF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'border-color .2s, color .2s',
            marginBottom: 20,
          }}
        >
          {isIt ? 'Hai già un account?' : isEs ? '¿Ya tienes una cuenta?' : 'Already have an account?'}
        </button>

        {/* Privacy & Terms */}
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 11, color: 'rgba(255,255,255,0.75)',
          textAlign: 'center', lineHeight: 1.5, margin: 0,
        }}>
          {isIt
            ? 'Continuando, accetti la nostra '
            : isEs
              ? 'Al continuar, aceptas nuestra '
              : 'By continuing, you agree to our '}
          <span
            onClick={() => setLegalModal('privacy')}
            style={{ color: '#FFFFFF', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Privacy Policy
          </span>
          {isIt ? ' e i ' : isEs ? ' y los ' : ' and '}
          <span
            onClick={() => setLegalModal('terms')}
            style={{ color: '#FFFFFF', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {isIt ? 'Termini di Servizio' : isEs ? 'Términos de Servicio' : 'Terms of Service'}
          </span>
          .
        </p>
      </motion.div>

      <AnimatePresence>
        {legalModal && (
          <LegalModal
            type={legalModal}
            lang={lang}
            onClose={() => setLegalModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GetStarted;
